import { DEEP7_MAUI_NUI_HARD_KM2 } from '../../sim/bottomfish';
import * as THREE from 'three';
import { TagMarks, visitorMark, type MarkTarget } from '../tagMarks';
import { HABITAT_NAMES, HabitatVisitors, visitorsNote, speciesVisitsNote, visitorAtOf, type SpeciesVisitNote, type Visitor, type VisitorAt } from './visitors';
import { hash3 } from '../fishSwim';
import { ROCK_DEG } from '../exhibitView';
import type { HourSun } from '../sunlight';
import type { WaterSceneData } from './data';
import { HabitatWater } from './water';
import { PaintedFloor, boulders, fogOnly, noise, squareGrid, type FloorShape } from './floor';
import { habitatFov, habitatLights, lightAt, marchComposition, type Composition, type HabitatPick, type HabitatScene } from './habitatScene';
import type { HabitatCameraProfile } from './host';
import { SlopeFish, type SlopeFishDrawn } from './slopeFish';
import { displayName, speciesOf } from '../../sim/species';

/**
 * The floor below the reef as a scene of its own (M110, the user's third brief, section 7, the framework: "Upper
 * Slope: rocky ledges, sloping basalt, deeper habitat organisms and dimmer water"; "Deep Ocean: sparse abyssal or
 * locally appropriate deep benthic habitat, sediment, rocks, darkness"; "do not fully populate them with new species
 * or elaborate art assets yet"): a stretch of floor at the habitat's depths, in the whole tank's paint (basalt where
 * it is steep, sand going to silt with depth), lit by what light reaches that deep, the water's fog and color the
 * sanctuary's, darker and closer with depth. Its visitors (M145) and, on the Slope since M215, the Deep 7 on its floor
 * (`SlopeFish`).
 *
 * Each a rendering choice (JUDGED): `ledges` the slope stepping down seaward (+x) in ledges `stepM` apart, each face
 * dropping `dropM` over its first `faceShare` (steep: rock) to a level top (sand), `rough` over it; `sediment` a soft plain, its swells `swellM` high; `sizeM`
 * the floor drawn on a grid of `gridM`, `texelM` its paint's texel; `boulders` on it; the camera `aboveM` over its
 * middle looking down-slope.
 */
const BENTHIC_SCENE = {
  sizeM: 220,
  gridM: 1.5,
  texelM: 0.12,
  ledges: { stepM: 9, dropM: 2.5, faceShare: 0.3, rough: 0.6 },
  sediment: { swellM: 0.8, cellM: 18, grade: 0.015 },
  boulders: { ledges: 40, sediment: 14, sizeM: [0.5, 2.4] },
  aboveM: 5.5,
  fov: { landscape: 46, portrait: 64 },
} as const;

/** The floor of a benthic habitat: at its depths' middle under the camera, falling seaward, in ledges or a soft plain. */
function benthicFloor(depthRangeM: readonly [number, number], style: 'ledges' | 'sediment', seed: number): FloorShape {
  const middle = (depthRangeM[0] + depthRangeM[1]) / 2;
  const [top, foot] = [Math.max(1.5, depthRangeM[0]), depthRangeM[1] + 40];
  if (style === 'ledges') {
    const l = BENTHIC_SCENE.ledges;
    return {
      heightAt(x, z) {
        // Step by step down: within each, a steep face over its first `faceShare`, then a level top.
        const along = x + 4 * (noise(seed, 1, x, z, 25) - 0.5);
        const step = Math.floor(along / l.stepM);
        const face = Math.min(1, (along / l.stepM - step) / l.faceShare);
        const depth = middle + l.dropM * (step + face * face * (3 - 2 * face)) + l.rough * (noise(seed, 2, x, z, 4) - 0.5);
        return -Math.min(foot, Math.max(top, depth));
      },
      pavementAt: () => 0,
    };
  }
  const s = BENTHIC_SCENE.sediment;
  return {
    heightAt(x, z) {
      return -Math.min(foot, Math.max(top, middle + s.grade * x + s.swellM * (noise(seed, 3, x, z, s.cellM) - 0.5) * 2));
    },
    pavementAt: () => 0,
  };
}

export class BenthicScene implements HabitatScene {
  readonly scene = new THREE.Scene();
  readonly camera = new THREE.PerspectiveCamera(50, 1, 0.1, 400);
  readonly profile: HabitatCameraProfile;
  readonly floor: FloorShape;
  private readonly painted: PaintedFloor;
  private readonly rocks: THREE.Mesh;
  private readonly water: HabitatWater;
  private readonly key: THREE.DirectionalLight;
  private data: WaterSceneData | null = null;
  /** The honu and the seals the day brings (M145). */
  private readonly visitors: HabitatVisitors;
  /** None of its own to mark (M156): no tagged animal is held at these depths. */
  readonly marks = new TagMarks();
  /** The Deep 7 on the Slope's floor (M215); none on the other floors. */
  private readonly slopeFish: SlopeFish | null;

  constructor(readonly kind: string, readonly locationName: string, readonly depthRangeM: readonly [number, number], readonly style: 'ledges' | 'sediment') {
    const s = BENTHIC_SCENE;
    const seed = Math.floor(hash3(kind.length, kind.charCodeAt(0) || 0, 151) * 2 ** 31);
    this.floor = benthicFloor(depthRangeM, style, seed);
    this.painted = new PaintedFloor(this.floor, squareGrid(s.sizeM, s.gridM), s.texelM, ROCK_DEG, depthRangeM[0] < 40);
    this.painted.tint((_p, c) => c.setRGB(1, 1, 1));
    const box = new THREE.Box3(new THREE.Vector3(-s.sizeM * 2, -(depthRangeM[1] + 200), -s.sizeM * 2), new THREE.Vector3(s.sizeM * 2, 0, s.sizeM * 2));
    // The surface's underside overhead only where the light from it still shows (a shallow site's rocky reef).
    this.water = new HabitatWater(box, depthRangeM[0] < 40, seed);
    this.water.fog(this.painted.skin);
    this.rocks = boulders(this.floor, seed, style === 'ledges' ? s.boulders.ledges : s.boulders.sediment, s.sizeM * 0.45, s.boulders.sizeM);
    this.water.fog(fogOnly(this.rocks.material as THREE.MeshStandardMaterial));
    const { key, fill } = habitatLights();
    this.key = key;
    this.scene.add(this.painted.mesh, this.rocks, this.water.group, key, key.target, fill);
    const target: [number, number, number] = [6, this.floor.heightAt(6, 0) + 1, 0];
    const position: [number, number, number] = [-8, Math.min(-1, this.floor.heightAt(-8, 4) + s.aboveM), 4];
    // The day's honu and seal across the home view (M145).
    this.visitors = new HabitatVisitors({ middle: new THREE.Vector3(...target), eye: new THREE.Vector3(...position), floorAt: (x, z) => this.floor.heightAt(x, z) });
    this.water.fog(this.visitors.fogUniforms());
    this.scene.add(this.visitors.group, this.marks.group);
    this.slopeFish = kind === 'slope' ? new SlopeFish({ middle: new THREE.Vector3(...target), eye: new THREE.Vector3(...position), floorAt: (x, z) => this.floor.heightAt(x, z) }) : null;
    if (this.slopeFish) {
      this.water.fog(this.slopeFish.fogUniforms());
      this.scene.add(this.slopeFish.group);
    }
    this.profile = {
      home: { position, target },
      fov: habitatFov(s.fov.landscape, s.fov.portrait),
      near: 0.1, far: 400,
      distance: [1.5, 40],
      polar: [0.2 * Math.PI, 0.6 * Math.PI],
      bounds: { min: [-60, -(depthRangeM[1] + 45), -60], max: [60, -1, 60] },
      clearance: 0.6,
    };
  }

  groundAt(x: number, z: number): number {
    return this.floor.heightAt(x, z);
  }

  /** Another world laid on the scene (a game loaded, a new one), its visitors' looks taken afresh (review 31). */
  newWorld(): void {
    this.visitors.newWorld();
  }

  setData(data: WaterSceneData): void {
    this.data = data;
    const e = data.environment;
    this.water.setState(e.kdPar.value, e.chlorophyllMgM3.value, e.zooMmolNPerM3.value);
    this.visitors.set(data.visitors);
    this.slopeFish?.set(data.bottomfish);
  }

  setLight(sun: HourSun): void {
    this.water.setLight(sun, -this.camera.position.y);
    const tint = lightAt(sun, this.key, this.water.seen);
    (this.rocks.material as THREE.MeshStandardMaterial).color.setRGB(...tint);
    this.visitors.setLight(...tint);
    this.slopeFish?.setLight(...tint);
    this.painted.setLight(sun);
  }

  animate(hour: number, _dayLengthHours: number, seconds: number, calm: boolean): void {
    this.water.follow(this.camera, this.camera.far * 0.8, seconds, calm);
    if (!calm) this.painted.drift(seconds);
    this.visitors.setHour(hour);
    this.visitors.swim(seconds, calm);
    this.slopeFish?.swim(seconds, calm);
  }

  fit(): void {
    // No level of detail: a visitor or two, each drawn whole, turned the way the camera sees it swim (M142).
    this.visitors.face(this.camera);
    this.slopeFish?.face(this.camera);
  }

  /**
   * The day's visitor under a point on the screen (M152), or a Deep 7 fish on the Slope's floor (M215), whichever is
   * nearer the camera where both are: each hides what stands behind it.
   */
  pick(x: number, y: number, widthPx: number, heightPx: number, withinPx: number): HabitatPick | null {
    const visitor = this.visitors.pick(this.camera, x, y, widthPx, heightPx, withinPx);
    const fish = this.slopeFish?.pick(this.camera, x, y, widthPx, heightPx, withinPx) ?? null;
    const away = (p: THREE.Vector3 | undefined): number => (p ? p.distanceTo(this.camera.position) : Infinity);
    if (fish && (!visitor || away(fish.middle) < away(this.visitors.drawnOf(visitor)?.middle))) return { bottomfish: fish.id };
    return visitor ? { visitor } : null;
  }

  /**
   * A Deep 7 fish by its name as drawn now, with its species' count and size today (M215); null where none is, or it is
   * fading out, gone from the day's count (M215's review: followed as it faded, its card read "one of 0").
   */
  slopeFishAt(id: string): (SlopeFishDrawn & { inHectare: number; perHa: number; kg: number }) | null {
    const drawn = this.slopeFish?.drawnOf(id) ?? null;
    const d = drawn && !drawn.leaving ? drawn : null;
    const day = d ? this.data?.bottomfish.find((b) => b.species === d.species) : undefined;
    return d && day ? { ...d, inHectare: day.count, perHa: day.perHa, kg: day.kg } : null;
  }

  handOverVisitor(from: string, to: string, tag?: Visitor['tag']): void {
    this.visitors.handOver(from, to, tag);
  }

  visitorAt(id: string): VisitorAt | null {
    const d = this.visitors.drawnOf(id);
    return d ? visitorAtOf(d, this.data?.visitOdds, this.data?.visits) : null;
  }

  visitOf(kind: Visitor['kind']): SpeciesVisitNote | null {
    return this.data?.visits.find((v) => v.species === kind) ?? null;
  }

  visitorGone(id: string): Visitor | null {
    return this.visitors.drawnOf(id) ? null : this.visitors.goneOf(id);
  }

  composition(cols = 30, rows = 50): Composition {
    return marchComposition(this.camera, (x, z) => this.floor.heightAt(x, z), () => false, 0, 1 / Math.max(1e-6, this.water.state().fogPerM), 0.15, cols, rows);
  }

  /** The water's state, for the tests. */
  waterState(): ReturnType<HabitatWater['state']> {
    return this.water.state();
  }

  note(): string {
    return [
      `the sanctuary's ${this.locationName}, a stretch of floor drawn as ${this.style === 'ledges' ? 'basalt ledges stepping down, sand on their tops' : 'soft sediment with a few rocks'} (the game knows no substrate: drawn, not simulated)`,
      'its light what reaches that deep at the sanctuary\'s clarity, drawn brighter than it is so it can be seen',
      ...(this.data?.visitOdds ? [visitorsNote(this.data.visitors, this.data.visitOdds)] : []),
      ...(this.data?.visits.length ? [speciesVisitsNote(this.data.visits, HABITAT_NAMES)] : []),
      ...(this.data?.bottomfish.length ? [`the Deep 7 on its floor, a sprite a fish, ${this.data.bottomfish.reduce((n, b) => n + b.count, 0)} in its hectare today, each species its fish a hectare of Maui Nui's ${DEEP7_MAUI_NUI_HARD_KM2} km² of hard bottom at 100 to 400 m (${this.data.bottomfish.map((b) => `${displayName(speciesOf(b.species))} ${b.perHa < 1 ? b.perHa.toFixed(2) : b.perHa.toFixed(1)}`).join(', ')}): the stock's biomass today, each species' fish its share of NOAA's 2024 survey's (Richards 2025), each drawn its species' mean size, the shallower up the slope and the deeper down it`] : []),
      ...(this.data?.notDrawn ?? []).map((n) => `not drawn: ${n}`),
    ].join('; ');
  }

  /** A tag's mark here over a tagged visitor drawn (M203), the only tagged animals the floor's scenes draw. */
  markAt(target: MarkTarget): THREE.Vector3 | null {
    return visitorMark(target, this.visitors);
  }

  dispose(): void {
    this.marks.dispose();
    this.painted.dispose();
    this.rocks.geometry.dispose();
    (this.rocks.material as THREE.Material).dispose();
    this.water.dispose();
    this.visitors.dispose();
    this.slopeFish?.dispose();
  }
}
